import type { FC, ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import { normalizeHeadlessData } from '../utils/normalize-headless-data';
import type { AudiFooterResponse } from '../types/audi-footer-response.types';

const ContentContext = createContext<AudiFooterResponse | undefined>(undefined);

export interface ContentContextProviderProps {
  readonly contentService: ContentServiceV1;
  readonly children?: ReactNode;
}

export const ContentContextProvider: FC<ContentContextProviderProps> = ({
  contentService,
  children,
}) => {
  const [content, setContent] = useState<AudiFooterResponse>(() =>
    normalizeHeadlessData(contentService),
  );

  useEffect(() => {
    const contentChangeHandler = () => {
      setContent(normalizeHeadlessData(contentService));
    };

    contentService.onContentChange(contentChangeHandler);

    return () => {
      contentService.removeOnContentChange(contentChangeHandler);
    };
  }, [contentService]);

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
};

export function useContent(): AudiFooterResponse {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error(
      'The context was not defined. Did you forget to wrap the feature app in <ContentContextProvider>?',
    );
  }
  return context;
}
